import axios from "axios";
import Axios from "axios";
import { User } from "../models/User";
import router from "../router/router";
import {AuthFormData} from "../pages/not-logged/auth/AuthForm";

/* RAG-36 : jeton d'appareil de confiance (« se souvenir de cet appareil 30 jours »). */
const TRUSTED_DEVICE_KEY = "trusted_device";

function authenticate(credentials: any) {
    // RAG-36 : étape 1 — le mot de passe ne délivre plus de jeton. Il déclenche
    // l'envoi d'un code par email et renvoie un jeton de challenge... SAUF si cet
    // appareil est de confiance : le jeton d'accès est alors délivré directement.
    const deviceToken = localStorage.getItem(TRUSTED_DEVICE_KEY);
    return axios
      .post(`${process.env.REACT_APP_API_URL}admins/login`, { ...credentials, device_token: deviceToken })
      .then((response: any) => {
        if (response.data.access_token) {
          localStorage.setItem("auth", JSON.stringify(response.data));
          return { loggedIn: true as const };
        }
        return { loggedIn: false as const, twoFactorToken: response.data.two_factor_token as string };
      });
}

function verifyCode(twoFactorToken: string, code: string, trustDevice: boolean) {
    // RAG-36 : étape 2 — valide le code reçu par email et récupère le jeton d'accès.
    // Si l'utilisateur a coché « faire confiance à cet appareil », on mémorise le
    // jeton d'appareil renvoyé pour sauter le code pendant 30 jours.
    return axios
      .post(`${process.env.REACT_APP_API_URL}admins/login/verify`, {
        two_factor_token: twoFactorToken,
        code,
        trust_device: trustDevice,
      })
      .then((response: any) => {
        localStorage.setItem("auth", JSON.stringify(response.data));
        if (response.data.device_token) {
          localStorage.setItem(TRUSTED_DEVICE_KEY, response.data.device_token);
        }
        return true;
      });
}

function resendCode(twoFactorToken: string) {
    // RAG-36 : renvoie un nouveau code (nouveau jeton de challenge en retour).
    return axios
      .post(`${process.env.REACT_APP_API_URL}admins/login/resend`, {
        two_factor_token: twoFactorToken,
      })
      .then((response: any) => response.data.two_factor_token as string);
}

function me() {
    console.log(process.env.REACT_APP_API_URL)
    return axios
      .get(`${process.env.REACT_APP_API_URL}admins/me`)
      .then((response: any) => new User(response.data));
}

async function updateAxiosAuthorization() {
    await Axios.interceptors.request.use((config: any) => {
      config.headers.Authorization = "Bearer " + getAuthToken();
  
      return config;
    });
  }
  
async function updateAxiosInterceptors() {
    await Axios.interceptors.response.use(undefined, (error) => {
        const { status } = error.response;
        if (status === 401) {
        logout();
        router.navigate("/login");
        }
        return Promise.reject(error);
    });
}

function getAuthToken() {
    let auth = localStorage.getItem("auth");

    return auth ? JSON.parse(auth).access_token : null;
}

async function logout() {
    window.localStorage.removeItem("auth");
  }

function loginWithGoogle(googleToken: string): Promise<void> {
    return new Promise((resolve, reject) => {
        axios.post(`${process.env.REACT_APP_API_URL}admins/login-with-google`, {google_token: googleToken})
            .then((response) => {
                localStorage.setItem("auth", JSON.stringify(response.data));

                resolve();
            })
            .catch((error) => {
                switch (error.response.status) {
                    case 401:
                        reject({status: 401, message: "Le jeton d'identification Google est invalide"});
                        break;
                    case 404:
                        reject({status: 404, message: "Utilisateur inconnu"});
                        break;
                    default:
                        reject({status: error.response.status, message: "Contactez un administrateur"})
                }
            })
    })
}

function resetPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
        axios.post(`${process.env.REACT_APP_API_URL}admins/ask-reset-password`, {email})
            .then((response) => {
                resolve()
            })
            .catch((error) => {
                switch (error.response.status) {
                    case 404: // Not found
                        reject({status: 404, message: "Utilisateur inconnu"});
                        break;
                    case 422:
                        reject({status: 422, message: "Email invalide"});
                        break;
                    default:
                        reject({status: error.response.status, message: "Veuillez réessayer"})
                }
            })
    })
}

// RAG-56 : message FR pour un mot de passe refusé par l'API (422). Les messages de validation
// de l'API sont en anglais ; on affiche la règle en clair (la validation côté écran l'évite déjà).
const PASSWORD_RULE_MESSAGE =
    "Mot de passe non conforme : au moins 12 caractères, avec une majuscule, une minuscule, un chiffre et un caractère spécial.";

function validateAccount(authFormData: AuthFormData): Promise<void> {
    return new Promise((resolve, reject) => {
        const body: any = {...authFormData};

        body.token_creating_account = body.token;

        delete body.token;

        axios.post(`${process.env.REACT_APP_API_URL}admins/validate-account`, body)
            .then((response) => {
                resolve();
            })
            .catch((error) => {
                switch (error.response.status) {
                    case 404:
                        reject({status: 404, message: "Compte inconnu"});
                        break;
                    case 422:
                        reject({status: 422, message: PASSWORD_RULE_MESSAGE});
                        break;
                    default:
                        reject({status: error.response.status, message: "Veuillez réessayer"});
                }
            })
    })
}

function validatePasswordAfterReset(authFormData: AuthFormData): Promise<void> {
    return new Promise((resolve, reject) => {
        const body: any = {...authFormData};

        body.token_resetting_password = body.token;

        delete body.token;

        axios.post(`${process.env.REACT_APP_API_URL}admins/validate-password`, body)
            .then((response) => {
                resolve();
            })
            .catch((error) => {
                switch (error.response.status) {
                    case 404:
                        reject({status: 404, message: "Compte inconnu"});
                        break;
                    case 403:
                        reject({status: 403, message: "Le jeton de réinitialisation a expiré"})
                        break;
                    case 422:
                        reject({status: 422, message: PASSWORD_RULE_MESSAGE});
                        break;
                    default:
                        reject({status: error.response.status, message: "Veuillez réessayer"});
                }
            });
    })
}

export default {
    authenticate,
    verifyCode,
    resendCode,
    me,
    logout,
    loginWithGoogle,
    resetPassword,
    validateAccount,
    validatePasswordAfterReset,
    updateAxiosAuthorization,
    updateAxiosInterceptors
}